import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { Description, Label, Radio, RadioGroup } from '@headlessui/react';
import { CheckCircleIcon, CheckIcon } from '@heroicons/react/20/solid';
import { ClockIcon } from '@heroicons/react/24/solid';
import {
  GET_CHECKOUT_CART,
  GET_SHIPPING_CARRIERS,
  UPDATE_CART,
} from '@retainerclub/customer-api';
import { useCart, useLocale } from '@retainerclub/hooks';
import PropTypes from 'prop-types';
import { useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';

function CheckoutFormDelivery({
  cartUpdatesLocked = false,
  deliveryUpdateState,
  onDeliveryUpdateStateChange,
  taxCheckoutEnabled = null,
}) {
  const apolloClient = useApolloClient();
  const { data: carriersData } = useQuery(GET_SHIPPING_CARRIERS);
  const [updateCart] = useMutation(UPDATE_CART, { fetchPolicy: 'no-cache' });

  const standardCarrierName = useMemo(() => {
    if (!carriersData?.shippingCarriers) {
      return '';
    }

    const carrier = carriersData.shippingCarriers.find(
      (c) => c.defaultStandard,
    );

    return carrier?.name || '';
  }, [carriersData]);

  const expeditedCarrierName = useMemo(() => {
    if (!carriersData?.shippingCarriers) {
      return '';
    }

    const carrier = carriersData.shippingCarriers.find(
      (c) => c.defaultExpedited,
    );

    return carrier?.name || '';
  }, [carriersData]);

  const deliveryMethods = useMemo(
    () => [
      {
        id: 'standard',
        title: 'Standard Delivery',
        turnaround: 'Estimated 7-10 calendar days',
        carrier: standardCarrierName || 'Standard',
        price: 0,
        benefits: [
          'Free shipping included',
          'Tracking number provided',
          'Economical choice for most customers',
        ],
      },
      {
        id: 'express',
        title: 'Express Delivery',
        turnaround: 'Estimated 7-8 calendar days',
        carrier: expeditedCarrierName
          ? `${expeditedCarrierName} 2 Day Service`
          : 'Expedited',
        price: 2500,
        benefits: [
          'Priority handling & faster delivery',
          'Enhanced tracking with real-time updates',
          'More secure & reliable delivery service',
          'Greater delivery accuracy & on-time performance',
          'Reduced risk of delays or lost packages',
        ],
      },
    ],
    [standardCarrierName, expeditedCarrierName],
  );

  const [selectedDeliveryMethod, setSelectedDeliveryMethod] = useState(
    deliveryMethods[0],
  );
  const deliveryReconciliationRef = useRef(null);
  const { locale } = useLocale();

  const { cart, updateCartLocal, cartId } = useCart();

  const { setValue } = useFormContext();

  function applyDeliveryState(deliveryMethod, authoritativeCart) {
    setSelectedDeliveryMethod(deliveryMethod);
    setValue('deliveryMethod', deliveryMethod.id, {
      shouldDirty: true,
      shouldValidate: true,
    });
    updateCartLocal(authoritativeCart);
  }

  function restoreDeliveryState(previousDeliveryMethod, previousCart) {
    setSelectedDeliveryMethod(previousDeliveryMethod);
    setValue('deliveryMethod', previousDeliveryMethod.id, {
      shouldDirty: true,
      shouldValidate: true,
    });
    if (previousCart?.id) {
      updateCartLocal(previousCart);
    }
  }

  async function reconcileDeliveryStatus() {
    if (!deliveryReconciliationRef.current) {
      return;
    }

    onDeliveryUpdateStateChange({
      blocked: true,
      error: null,
      loading: true,
    });

    try {
      const response = await apolloClient.query({
        query: GET_CHECKOUT_CART,
        variables: { cartId },
        fetchPolicy: 'network-only',
      });
      const authoritativeCart = response?.data?.checkoutCart;
      const matchingDeliveryMethods = deliveryMethods.filter(
        (deliveryMethod) =>
          deliveryMethod.price === authoritativeCart?.shippingAmount,
      );

      if (
        authoritativeCart?.id !== cartId ||
        matchingDeliveryMethods.length !== 1
      ) {
        throw new Error('Authoritative delivery method could not be mapped.');
      }

      applyDeliveryState(matchingDeliveryMethods[0], authoritativeCart);
      deliveryReconciliationRef.current = null;
      onDeliveryUpdateStateChange({
        blocked: false,
        error: null,
        loading: false,
      });
    } catch (_error) {
      onDeliveryUpdateStateChange({
        blocked: true,
        error:
          'We could not confirm your saved delivery method. Retry before placing your order, or contact Retainer Club support for help.',
        loading: false,
      });
    }
  }

  async function handleDeliveryMethodChange(deliveryMethod) {
    if (deliveryUpdateState.blocked) {
      return;
    }

    const newDeliveryMethod = deliveryMethods.find(
      (d) => d.id === deliveryMethod,
    );
    const previousCart = cart;
    const previousDeliveryMethod = selectedDeliveryMethod;

    setSelectedDeliveryMethod(newDeliveryMethod);
    setValue('deliveryMethod', newDeliveryMethod.id, {
      shouldDirty: true,
      shouldValidate: true,
    });

    if (cartUpdatesLocked) {
      deliveryReconciliationRef.current = null;
      onDeliveryUpdateStateChange({
        blocked: false,
        error: null,
        loading: false,
      });
      return;
    }

    if (taxCheckoutEnabled === false) {
      deliveryReconciliationRef.current = {
        previousCart,
        previousDeliveryMethod,
      };
      onDeliveryUpdateStateChange({
        blocked: true,
        error: null,
        loading: true,
      });
      try {
        const response = await updateCart({
          variables: {
            input: {
              input: {
                id: cartId,
                shippingAmount: newDeliveryMethod.price,
              },
            },
          },
        });
        const payload = response?.data?.updateCart;
        if (payload?.errorMessages?.length > 0) {
          restoreDeliveryState(previousDeliveryMethod, previousCart);
          deliveryReconciliationRef.current = null;
          onDeliveryUpdateStateChange({
            blocked: false,
            error:
              'Delivery method could not be updated. Your previous selection has been restored.',
            loading: false,
          });
          return;
        }

        if (!payload?.cart) {
          await reconcileDeliveryStatus();
          return;
        }

        updateCartLocal(payload.cart);
        deliveryReconciliationRef.current = null;
        onDeliveryUpdateStateChange({
          blocked: false,
          error: null,
          loading: false,
        });
      } catch (_error) {
        await reconcileDeliveryStatus();
      }
      return;
    }

    deliveryReconciliationRef.current = null;
    onDeliveryUpdateStateChange({
      blocked: false,
      error: null,
      loading: false,
    });
  }

  return (
    <div className="mt-4 border-rcgray-300 border-t pt-4">
      <RadioGroup
        value={selectedDeliveryMethod.id}
        disabled={
          taxCheckoutEnabled === null ||
          taxCheckoutEnabled === undefined ||
          deliveryUpdateState.blocked
        }
        onChange={(deliveryMethod) =>
          handleDeliveryMethodChange(deliveryMethod)
        }
      >
        <Label className="font-medium text-gray-900 text-lg">
          Choose Your Delivery Method
        </Label>
        <div className="mt-4 border-yellow-400 border-l-4 bg-yellow-50 p-4">
          <div className="flex">
            <div className="shrink-0">
              <ClockIcon
                aria-hidden="true"
                className="size-5 text-yellow-400"
              />
            </div>
            <div className="ml-3">
              <p className="text-xs text-yellow-700">
                <span className="font-medium">Production Time:</span> Your
                custom retainer requires 4 business days for production. This
                begins once we receive your 3D scan and order confirmation. The
                delivery times shown below include the production period.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-4">
          {deliveryMethods.map((deliveryMethod) => (
            <Radio
              key={deliveryMethod.id}
              value={deliveryMethod.id}
              checked={selectedDeliveryMethod.id === deliveryMethod.id}
              className="group relative flex cursor-pointer rounded-lg border border-gray-300 bg-white p-4 shadow-xs focus:outline-hidden data-checked:border-transparent data-checked:bg-rcprimary-50 data-active:ring-2 data-active:ring-rcprimary-500"
            >
              <span className="flex flex-1">
                <span className="flex flex-1 flex-col">
                  <Label
                    as="span"
                    className="block font-medium text-gray-900 text-md"
                  >
                    {deliveryMethod.title}
                  </Label>
                  <Description
                    as="span"
                    className="mt-1 flex items-center text-gray-500 text-sm"
                  >
                    {deliveryMethod.turnaround}
                  </Description>
                  <Description
                    as="span"
                    className="mt-1 flex items-center text-gray-500 text-md"
                  >
                    {deliveryMethod.carrier}
                  </Description>
                  <div className="my-2 border-gray-200 border-t" />
                  <Description
                    as="span"
                    className="mt-1 mb-2 flex items-center text-gray-500 text-xs"
                  >
                    <ul className="list-inside">
                      {deliveryMethod.benefits.map((benefit) => (
                        <li key={benefit} className="flex items-start">
                          <CheckIcon
                            aria-hidden="true"
                            className="mr-2 size-4 shrink-0 text-green-500"
                          />
                          <span>{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </Description>
                  <Description
                    as="span"
                    className="mt-auto font-medium text-gray-900 text-sm"
                  >
                    {deliveryMethod.price === 0 ? (
                      <span className="font-bold text-green-500">FREE</span>
                    ) : taxCheckoutEnabled ? (
                      selectedDeliveryMethod.id === deliveryMethod.id &&
                      cart?.shippingAmount > 0 ? (
                        (cart.shippingAmount / 100).toLocaleString(locale, {
                          style: 'currency',
                          currency: 'USD',
                        })
                      ) : (
                        'Calculated at checkout'
                      )
                    ) : (
                      `${(deliveryMethod.price / 100).toLocaleString(locale, {
                        style: 'currency',
                        currency: 'USD',
                      })}`
                    )}
                  </Description>
                </span>
              </span>

              <CheckCircleIcon
                className="invisible size-5 text-rcprimary-600 group-data-checked:visible"
                aria-hidden="true"
              />

              <span
                className="pointer-events-none absolute -inset-px rounded-lg border-2 border-transparent data-active:border group-data-checked:border-rcprimary-500"
                aria-hidden="true"
              />
            </Radio>
          ))}
        </div>
      </RadioGroup>
      {deliveryUpdateState.loading ? (
        <p
          className="mt-4 text-gray-600 text-sm"
          aria-live="polite"
          role="status"
        >
          Saving and confirming your delivery method.
        </p>
      ) : null}
      {deliveryUpdateState.error ? (
        <div className="mt-4 text-sm">
          <p className="text-red-700" role="alert">
            {deliveryUpdateState.error}
          </p>
          {deliveryUpdateState.blocked ? (
            <button
              type="button"
              className="mt-2 font-medium text-rcprimary-500 underline hover:text-rcprimary-400 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-rcprimary-500 focus-visible:ring-offset-2"
              onClick={reconcileDeliveryStatus}
            >
              Retry delivery status
            </button>
          ) : null}
        </div>
      ) : null}
      <p className="mt-4 text-gray-500 text-xs">
        <span className="font-medium">Important:</span> Total delivery timeframe
        shown above includes both production (4 business days) and shipping
        time. Estimates are in calendar days and may vary based on your
        location, carrier conditions, and holidays. Orders placed after 2 PM EST
        are processed the next business day. Weekend orders begin processing on
        Monday.
      </p>
    </div>
  );
}

CheckoutFormDelivery.propTypes = {
  cartUpdatesLocked: PropTypes.bool,
  deliveryUpdateState: PropTypes.shape({
    blocked: PropTypes.bool.isRequired,
    error: PropTypes.string,
    loading: PropTypes.bool.isRequired,
  }).isRequired,
  onDeliveryUpdateStateChange: PropTypes.func.isRequired,
  taxCheckoutEnabled: PropTypes.bool,
};

export default CheckoutFormDelivery;
